// Путь: D:\QR\QR-WEB\dashboard\src\components\Dashboard\Dashboard.js
// Название: User Dashboard v7.0 - Clean Dashboard
// Версия: 7.0.0
// Дата: 24.01.2026

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BusinessDashboard from './BusinessDashboard';
import AdminDashboard from './AdminDashboard';
import './Dashboard.css';

const API_URL = 'https://qr-money.onrender.com/api';

export const Dashboard = () => {
  // ============================================
  // РОУТИНГ ПО РОЛЯМ
  // ============================================
  const userRole = localStorage.getItem('userRole') || 'user';
  if (userRole === 'admin') return <AdminDashboard />;
  if (userRole === 'business') return <BusinessDashboard />;

  // ============================================
  // STATE
  // ============================================
  const [user, setUser] = useState(null);
  const [totalBrandDar, setTotalBrandDar] = useState(0);
  const [brandBalances, setBrandBalances] = useState([]);
  const [giftsCount, setGiftsCount] = useState(0);
  const [promocodesCount, setPromocodesCount] = useState(0);
  const [referralStats, setReferralStats] = useState({ used: 0, limit: 3 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // ============================================
  // ЗАГРУЗКА ДАННЫХ
  // ============================================
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      if (!token || !userId) { navigate('/login'); return; }

      // Профиль
      const profileRes = await fetch(`${API_URL}/profile/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const profileData = await profileRes.json();
      if (profileData.data) setUser(profileData.data);

      // Brand балансы
      try {
        const brandRes = await fetch(`${API_URL}/brand-store/my-balances`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const brandData = await brandRes.json();
        if (brandData.success) {
          const withBalance = (brandData.data.balances || []).filter(b => parseFloat(b.amount) > 0);
          setBrandBalances(withBalance);
          setTotalBrandDar(parseFloat(brandData.data.totalBrandDar || 0));
        }
      } catch (e) {}

      // Мои промокоды
      try {
        const promoRes = await fetch(`${API_URL}/promocodes/my`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const promoData = await promoRes.json();
        if (promoData.success) {
          setPromocodesCount((promoData.data || []).length);
        }
      } catch (e) {}

      // Brand подарки (с фильтрацией скрытых)
      try {
        let hidden = [];
        try {
          const hiddenRes = await fetch(`${API_URL}/profile/hidden-items`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const hiddenData = await hiddenRes.json();
          if (hiddenData.success) hidden = hiddenData.data || [];
        } catch (e) {}

        const isHidden = (type, id) => hidden.some(h => h.item_type === type && String(h.item_id) === String(id));

        const brandGiftsRes = await fetch(`${API_URL}/brand-gifts/my`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const brandGiftsData = await brandGiftsRes.json();
        if (brandGiftsData.success) {
          const visible = (brandGiftsData.gifts || []).filter(g => !isHidden('brand_gift', g.id));
          setGiftsCount(visible.length);
        }
      } catch (e) {}

      // Referral stats
      try {
        const refRes = await fetch(`${API_URL}/referrals/my-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const refData = await refRes.json();
        if (refData.success) {
          setReferralStats({
            used: refData.data?.totalInvites || 0,
            limit: 3
          });
        }
      } catch (e) {}

    } catch (error) {
      console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // SMART TIP LOGIC
  // ============================================
  const getSmartTip = () => {
    if (totalBrandDar === 0 && giftsCount === 0) {
      return {
        icon: '💡',
        text: 'Активируй свой первый подарок и получи DAR!',
        action: () => navigate('/vouchers/claim'),
        btn: 'Активировать'
      };
    }
    if (totalBrandDar > 0) {
      return {
        icon: '🎯',
        text: `У тебя ${totalBrandDar.toFixed(0)} DAR! Обменяй на промокод со скидкой.`,
        action: () => navigate('/brand-store'),
        btn: 'В Brand Store'
      };
    }
    if (promocodesCount > 0) {
      return {
        icon: '🎟️',
        text: `У тебя ${promocodesCount} промокод(ов). Используй их для скидок!`,
        action: () => navigate('/my-promocodes'),
        btn: 'Мои промокоды'
      };
    }
    return {
      icon: '✨',
      text: 'Следи за новыми кампаниями от брендов!',
      action: () => navigate('/brand-store'),
      btn: 'Смотреть'
    };
  };

  // ============================================
  // HELPERS
  // ============================================
  const getInitials = (name, email) => {
    if (name) return name.charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return 'U';
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) {
    return (
      <div className="sd-loading">
        <div className="sd-spinner"></div>
      </div>
    );
  }

  const smartTip = getSmartTip();

  return (
    <div className="sd-page">
      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <div className="sd-hero">
        <div className="sd-hero-left">
          <div className="sd-avatar">
            {getInitials(user?.first_name, user?.email)}
          </div>
          <div className="sd-greeting">
            <span className="sd-hello">Привет, {user?.first_name || 'друг'}! 👋</span>
            <span className="sd-subtitle">Добро пожаловать в DARY</span>
          </div>
        </div>
        <div className="sd-hero-badges">
          <div className="sd-badge sd-badge-dar">
            <span className="sd-badge-icon">💰</span>
            <span className="sd-badge-value">{totalBrandDar.toFixed(0)}</span>
            <span className="sd-badge-label">DAR</span>
          </div>
          <div className="sd-badge" onClick={() => navigate('/vouchers/my')}>
            <span className="sd-badge-icon">🎁</span>
            <span className="sd-badge-value">{giftsCount}</span>
          </div>
          <div className="sd-badge" onClick={() => navigate('/my-promocodes')}>
            <span className="sd-badge-icon">🎟️</span>
            <span className="sd-badge-value">{promocodesCount}</span>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* QUICK ACTIONS */}
      {/* ============================================ */}
      <div className="sd-actions">
        <div className="sd-action-card sd-action-claim" onClick={() => navigate('/vouchers/claim')}>
          <div className="sd-action-icon">📥</div>
          <div className="sd-action-text">
            <span className="sd-action-title">Получить</span>
            <span className="sd-action-desc">Активировать подарок</span>
          </div>
          <div className="sd-action-arrow">→</div>
        </div>
        <div className="sd-action-card sd-action-gifts" onClick={() => navigate('/vouchers/my')}>
          <div className="sd-action-icon">🎁</div>
          <div className="sd-action-text">
            <span className="sd-action-title">Мои подарки</span>
            <span className="sd-action-desc">Получено: {giftsCount}</span>
          </div>
          <div className="sd-action-arrow">→</div>
        </div>
        <div className="sd-action-card sd-action-codes" onClick={() => navigate('/my-promocodes')}>
          <div className="sd-action-icon">🎟️</div>
          <div className="sd-action-text">
            <span className="sd-action-title">Промокоды</span>
            <span className="sd-action-desc">Получено: {promocodesCount}</span>
          </div>
          <div className="sd-action-arrow">→</div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SMART TIP */}
      {/* ============================================ */}
      <div className="sd-tip">
        <div className="sd-tip-icon">{smartTip.icon}</div>
        <div className="sd-tip-text">{smartTip.text}</div>
        <button className="sd-tip-btn" onClick={smartTip.action}>{smartTip.btn}</button>
      </div>

      {/* ============================================ */}
      {/* REFERRALS */}
      {/* ============================================ */}
      <div className="sd-referrals">
        <div className="sd-ref-left">
          <div className="sd-ref-icon">👥</div>
          <div className="sd-ref-info">
            <span className="sd-ref-title">Пригласи друзей</span>
            <span className="sd-ref-bonus">+5 DAR тебе и другу</span>
          </div>
        </div>
        <div className="sd-ref-right">
          <div className="sd-ref-progress">
            {[...Array(referralStats.limit)].map((_, i) => (
              <div 
                key={i} 
                className={`sd-ref-dot ${i < referralStats.used ? 'sd-ref-dot-used' : ''}`}
              ></div>
            ))}
            <span className="sd-ref-count">{referralStats.used}/{referralStats.limit}</span>
          </div>
          <button className="sd-ref-btn" onClick={() => navigate('/referrals')}>
            Пригласить →
          </button>
        </div>
      </div>

      {/* ============================================ */}
      {/* MY BRANDS (если есть баланс) */}
      {/* ============================================ */}
      {brandBalances.length > 0 && (
        <div className="sd-brands">
          <div className="sd-section-header">
            <h3>🏪 Мои бренды</h3>
            <button className="sd-link" onClick={() => navigate('/brand-store')}>Все →</button>
          </div>
          <div className="sd-brands-list">
            {brandBalances.slice(0, 4).map((brand, i) => (
              <div 
                key={i} 
                className="sd-brand-card"
                onClick={() => navigate(`/brand-store/${brand.brand_slug}`)}
              >
                <span className="sd-brand-emoji">{brand.logo_emoji || '🏷️'}</span>
                <span className="sd-brand-name">{brand.brand_name}</span>
                <span className="sd-brand-dar">{parseFloat(brand.amount).toFixed(0)} DAR</span>
                <span className="sd-brand-arrow">→</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;